import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { useUser } from '../context/UserContext';
import Mixpanel from '../utils/mixpanel';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

// Utility function for token retrieval and validation
const getTokenOrRedirect = (setError, navigate, logger) => {
  const token = localStorage.getItem('token');
  if (!token) {
    logger.error('No authentication token found in localStorage.');
    setError('Authentication error: Please log in again.');
    navigate('/login');
    return null;
  }
  logger.debug('Authentication token found in localStorage.');
  return token;
};

const Onboarding = ({ startStep = 0 }) => {
  const { user, updateProfile } = useUser();
  const navigate = useNavigate();
  const [step, setStep] = useState(startStep);
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [preparingProgram, setPreparingProgram] = useState(false);
  const [userName, setUserName] = useState('');
  const [content, setContent] = useState({
    introduction: [
      "Welcome to Mental Edge Coach! We're excited to help you improve your mental performance.",
      "This app is designed to help athletes like you develop mental skills that translate to better performance.",
      "Let's get started by understanding more about you and your goals."
    ],
    why_i_play: [
      "I play because I love the competition and challenge.",
      "I play to be part of a team and build lasting relationships.",
      "I play to push myself to be the best I can be.",
      "I play because it's fun and brings me joy."
    ],
    journey: [
      "I'm new to competitive sports and still learning the basics.",
      "I play recreationally and enjoy the social aspects of my sport.",
      "I'm a competitive athlete focused on improving my performance.",
      "I'm an elite athlete competing at a high level.",
      "I'm a coach helping athletes improve their mental performance."
    ],
    goals: [
      "Improve my focus during competition",
      "Manage performance anxiety better",
      "Build more confidence in my abilities",
      "Develop a consistent pre-performance routine",
      "Improve my resilience after setbacks",
      "Enhance my team leadership skills"
    ],
    expectations: [
      "Provide practical mental skills exercises",
      "Help me track my mental performance progress",
      "Offer personalized guidance based on my needs",
      "Give me tools to prepare for competition",
      "Help me reflect on my performances",
      "Provide a structured approach to mental training"
    ],
    current_mental_state: [
      "I feel confident and focused during competition.",
      "I feel anxious and uncertain about my abilities.",
      "I feel motivated and driven to succeed.",
      "I feel overwhelmed and struggling to cope."
    ],
    desired_mental_state: [
      "I want to feel more confident and focused during performance.",
      "I want to feel more calm and composed under pressure.",
      "I want to feel more motivated and driven to succeed.",
      "I want to feel more resilient and better able to bounce back from setbacks."
    ]
  });
  const [responses, setResponses] = useState({
    whyIPlaySport: '',
    journey: '',
    goals: '',
    expectations: '',
    currentMentalState: '',
    desiredMentalState: '',
    customStatements: {
      journey: [],
      goals: [],
      expectations: [],
      current_mental_state: [],
      desired_mental_state: []
    }
  });

  // State for tracking AI generation progress
  const [mvpGenerated, setMvpGenerated] = useState(false);
  const [mindsetGenerated, setMindsetGenerated] = useState(false);
  const [habitsGenerated, setHabitsGenerated] = useState(false);
  const [audioGenerated, setAudioGenerated] = useState(false);
  const [mentalImageryGenerated, setMentalImageryGenerated] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  
  // Use a ref to track if the user has clicked the continue button
  const userClickedContinue = useRef(false);

  useEffect(() => {
    // Log when generationComplete changes
    logger.debug('generationComplete state changed:', generationComplete);
    
    if (generationComplete) {
      logger.debug('Generation is complete, "Continue to Dashboard" button should be visible');
    }
  }, [generationComplete]);

  const steps = [
    { title: 'Introduction', field: 'introduction', description: '', instruction:'' },
    { title: 'Your Motivation', field: 'why_i_play', description: '', instruction: 'Tell us what motivates you and why you enjoy your sport.' },
    { title: 'Your Journey', field: 'journey', description: 'Where are you in your athletic journey?', instruction: 'Select the statement that best describes your current stage.' },
    { title: 'Your Performance Goals', field: 'goals', description: 'What performance outcomes do you want to achieve?', instruction: 'Choose the performance goal that is most important to your athletic development right now.' },
    { title: 'Current Mental State', field: 'current_mental_state', description: 'How do you feel mentally?', instruction: 'Select the statement that best describes your current mental state during performance.' },
    { title: 'Desired Mental State', field: 'desired_mental_state', description: 'How do you want to feel?', instruction: 'Choose how you would like to feel mentally during performance.' },
    { title: 'App Expectations', field: 'expectations', description: '', instruction: 'Select what you most want to get from using this app.' }
  ];

  const currentStep = steps[step] || steps[0];

  useEffect(() => {
    try {
      Mixpanel.trackFeatureUsed('Onboarding', 'Opened');
      
      // Only fetch content if we don't already have it
      if (!content[currentStep.field] || content[currentStep.field].length === 0) {
        fetchContent(currentStep.field);
      }
    } catch (error) {
      logger.error('Error in Onboarding useEffect:', error);
    }
  }, [currentStep]);

  useEffect(() => {
    // Clear cached content on component mount for testing
    localStorage.removeItem('onboardingContent');
    logger.debug('Cleared cached onboarding content for testing');
    
    // Track onboarding view
    Mixpanel.track('View Onboarding');
    
    // Get user name for personalized messages
    const storedName = localStorage.getItem('userName');
    if (storedName) {
      setUserName(storedName);
    } else if (user && user.name) {
      setUserName(user.name.split(' ')[0]); // Get first name
      localStorage.setItem('userName', user.name.split(' ')[0]);
    }
  }, [user]);

  const fetchContent = async (stepName) => {
    setContentLoading(true);
    setError('');
    
    // Define fallback content for each step in case API fails
    const fallbackContent = {
      introduction: [
        "Welcome to Mental Edge Coach! We're excited to help you improve your mental performance.",
        "This app is designed to help athletes like you develop mental skills that translate to better performance.",
        "Let's get started by understanding more about you and your goals."
      ],
      why_i_play: [
        "I play because I love the competition and challenge.",
        "I play to be part of a team and build lasting relationships.",
        "I play to push myself to be the best I can be.",
        "I play because it's fun and brings me joy."
      ],
      journey: [
        "I'm new to competitive sports and still learning the basics.",
        "I play recreationally and enjoy the social aspects of my sport.",
        "I'm a competitive athlete focused on improving my performance.",
        "I'm an elite athlete competing at a high level.",
        "I'm a coach helping athletes improve their mental performance."
      ],
      goals: [
        "Improve my focus during competition",
        "Manage performance anxiety better",
        "Build more confidence in my abilities",
        "Develop a consistent pre-performance routine",
        "Improve my resilience after setbacks",
        "Enhance my team leadership skills"
      ],
      expectations: [
        "Provide practical mental skills exercises",
        "Help me track my mental performance progress",
        "Offer personalized guidance based on my needs",
        "Give me tools to prepare for competition",
        "Help me reflect on my performances",
        "Provide a structured approach to mental training"
      ],
      current_mental_state: [
        "I feel confident and focused during competition.",
        "I feel anxious and uncertain about my abilities.",
        "I feel motivated and driven to succeed.",
        "I feel overwhelmed and struggling to cope."
      ],
      desired_mental_state: [
        "I want to feel more confident and focused during performance.",
        "I want to feel more calm and composed under pressure.",
        "I want to feel more motivated and driven to succeed.",
        "I want to feel more resilient and better able to bounce back from setbacks."
      ]
    };
    
    const token = getTokenOrRedirect(setError, navigate, logger);
    if (!token) {
      setContentLoading(false);
      return;
    }
    try {
      logger.debug(`Fetching AI-generated content for ${stepName} step`);
      const response = await axios.post(
        `${API_URL}/api/onboarding/generate`,
        { step: stepName },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.content) {
        // If the response contains an array of content items, use them
        if (Array.isArray(response.data.content)) {
          logger.debug(`Received ${response.data.content.length} AI-generated items for ${stepName}`);
          setContent(prev => ({
            ...prev,
            [stepName]: response.data.content
          }));
        } 
        // If the response contains a string, split it into an array by newlines
        else if (typeof response.data.content === 'string') {
          const contentItems = response.data.content
            .split('\n')
            .map(item => item.trim())
            .filter(item => item.length > 0);
          
          logger.debug(`Parsed ${contentItems.length} AI-generated items from string for ${stepName}`);
          setContent(prev => ({
            ...prev,
            [stepName]: contentItems
          }));
        }
        // If the response contains an object with specific fields
        else {
          logger.debug(`Received object format for ${stepName}, using as is`);
          setContent(prev => ({
            ...prev,
            [stepName]: response.data.content
          }));
        }
      } else {
        throw new Error('Invalid response format from AI API');
      }
    } catch (error) {
      logger.error(`Error fetching AI content for ${stepName}:`, error);
      
      // Use fallback content if API call fails
      logger.debug(`Using fallback content for ${stepName} step due to error`);
      setContent(prev => ({
        ...prev,
        [stepName]: fallbackContent[stepName] || []
      }));
    } finally {
      setContentLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Map snake_case field names to camelCase for the API
    const fieldMapping = {
      'current_mental_state': 'currentMentalState',
      'desired_mental_state': 'desiredMentalState',
      'journey': 'journey',
      'goals': 'goals',
      'expectations': 'expectations',
      'why_i_play': 'whyIPlaySport'
    };
    
    // Use the mapped field name or the original name if no mapping exists
    const apiFieldName = fieldMapping[name] || name;
    
    logger.debug(`Setting ${name} as ${apiFieldName} with value:`, value);
    
    setResponses(prev => ({
      ...prev,
      [apiFieldName]: value
    }));
  };

  const handleAddCustomStatement = (stepName) => {
    if (!customInput.trim()) {
      return;
    }
    
    // Map snake_case field names to camelCase for the API
    const fieldMapping = {
      'current_mental_state': 'currentMentalState',
      'desired_mental_state': 'desiredMentalState',
      'journey': 'journey',
      'goals': 'goals',
      'expectations': 'expectations'
    };
    
    const apiFieldName = fieldMapping[stepName] || stepName;
    const customValue = customInput.trim();
    
    // Add the custom statement to the customStatements array
    setResponses(prev => ({
      ...prev,
      customStatements: {
        ...prev.customStatements,
        [stepName]: [...prev.customStatements[stepName], customValue]
      },
      // Automatically select the custom statement as the current selection
      [apiFieldName]: customValue
    }));
    
    logger.debug(`Added and selected custom statement for ${stepName} as ${apiFieldName}:`, customValue);
    
    setCustomInput('');
  };

  const handleRemoveCustomStatement = (stepName, index) => {
    setResponses(prev => ({
      ...prev,
      customStatements: {
        ...prev.customStatements,
        [stepName]: prev.customStatements[stepName].filter((_, i) => i !== index)
      }
    }));
  };

  const handleSelectCustomStatement = (stepName, statement) => {
    setResponses(prev => ({
      ...prev,
      [stepName]: statement
    }));
  };

  const renderIntroduction = () => {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="flex items-center mb-1">
                <h4 className="font-bold">Personalized Experience</h4>
              </div>
              <p className="text-sm">Get customized mental training based on your sport and goals.</p>
            </div>
            
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="flex items-center mb-1">
                <h4 className="font-bold">Mental Skills Training</h4>
              </div>
              <p className="text-sm">Develop confidence, focus, and resilience through proven techniques.</p>
            </div>
          </div>
          
          <h4 className="font-bold mb-1">What You'll Get:</h4>
          <ul className="list-disc list-inside space-y-1 mb-2 text-sm">
            <li>Personalized mental training exercises</li>
            <li>Goal setting and tracking tools</li>
            <li>Performance reflection and analysis</li>
            <li>Mindset development techniques</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderWhyIPlayInput = () => {
    return (
      <div className="space-y-4">
        <textarea
          value={responses.whyIPlaySport}
          onChange={(e) => setResponses({
            ...responses,
            whyIPlaySport: e.target.value
          })}
          placeholder={`I enjoy playing ${user?.sport || 'my sport'} because...`}
          className="textarea textarea-bordered w-full min-h-[120px]"
        />
        
        {responses.whyIPlaySport.length === 0 && (
          <p className="text-sm text-yellow-600">
            Please share why you enjoy your sport before proceeding.
          </p>
        )}
      </div>
    );
  };

  const renderStatements = (stepName) => {
    if (contentLoading) {
      return (
        <div className="flex justify-center items-center py-8">
          <span className="ml-2 text-gray-600">Loading suggestions...</span>
        </div>
      );
    }
    
    const statements = content[stepName] || [];
    
    // Map snake_case field names to camelCase for accessing the correct state values
    const fieldMapping = {
      'current_mental_state': 'currentMentalState',
      'desired_mental_state': 'desiredMentalState',
      'journey': 'journey',
      'goals': 'goals',
      'expectations': 'expectations'
    };
    
    // Use the mapped field name to get the current selection
    const apiFieldName = fieldMapping[stepName] || stepName;
    const currentSelection = responses[apiFieldName] || '';
    
    logger.debug(`Rendering ${stepName} with current selection:`, currentSelection);
    
    const customStatements = responses.customStatements[stepName] || [];
    
    return (
      <div className="space-y-6">
        {/* Suggested Options */}
        <div className="space-y-4">
          <h4 className="font-medium">Suggested Options</h4>
          
          {statements.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {statements.map((statement, idx) => (
                <div
                  key={idx}
                  onClick={() => handleInputChange({ target: { name: stepName, value: statement } })}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    currentSelection === statement
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-6 h-6 flex-shrink-0 rounded-full border flex items-center justify-center mr-3 ${
                      currentSelection === statement
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'border-gray-300'
                    }`}>
                      {currentSelection === statement && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <span className="text-sm">{statement}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600">No suggestions available</p>
            </div>
          )}
        </div>

        {/* Custom Statements */}
        {customStatements.length > 0 && (
          <div className="mt-6 space-y-4">
            <h4 className="font-medium">Your Custom Options</h4>
            <div className="grid grid-cols-1 gap-3">
              {customStatements.map((statement, idx) => (
                <div
                  key={`custom-${idx}`}
                  onClick={() => handleInputChange({ target: { name: stepName, value: statement } })}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    currentSelection === statement
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className={`w-6 h-6 flex-shrink-0 rounded-full border flex items-center justify-center mr-3 ${
                        currentSelection === statement
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'border-gray-300'
                      }`}>
                        {currentSelection === statement && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <span className="text-sm">{statement}</span>
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveCustomStatement(stepName, idx);
                      }}
                      className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10 ml-2"
                      aria-label="Remove statement"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom Option Input */}
        <div className="mt-8 space-y-4">
          <h4 className="font-medium">Or Add Your Own Option</h4>
          
          <div className="flex items-start gap-2">
            <textarea
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              className="textarea textarea-bordered flex-1 min-h-[5rem] text-sm resize-none"
              placeholder="Enter your own option..."
            />
          </div>
          
          <button
            onClick={() => handleAddCustomStatement(stepName)}
            disabled={!customInput.trim()}
            className="btn btn-primary btn-sm w-full"
          >
            Add & Select Custom Option
          </button>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep.field) {
      case 'introduction':
        return renderIntroduction();
      case 'why_i_play':
        return renderWhyIPlayInput();
      case 'journey':
      case 'goals':
      case 'expectations':
      case 'current_mental_state':
      case 'desired_mental_state':
        return renderStatements(currentStep.field);
      default:
        return (
          <div className="alert alert-warning">
            <p>Unknown step: {currentStep.field}</p>
          </div>
        );
    }
  };

  const handleNext = () => {
    // Validate current step
    if(currentStep.field === 'why_i_play' && !responses.whyIPlaySport.trim()) {
      setError('Please tell us why you play your sport before proceeding.');
      return;
    }
    
    // Map snake_case field names to camelCase for validation
    const fieldMapping = {
      'current_mental_state': 'currentMentalState',
      'desired_mental_state': 'desiredMentalState',
      'journey': 'journey',
      'goals': 'goals',
      'expectations': 'expectations'
    };
    
    if (['journey', 'goals', 'expectations', 'current_mental_state', 'desired_mental_state'].includes(currentStep.field)) {
      const apiFieldName = fieldMapping[currentStep.field] || currentStep.field;
      
      logger.debug(`Validating ${currentStep.field} as ${apiFieldName}:`, responses[apiFieldName]);
      
      if (!responses[apiFieldName]) {
        setError('Please select an option before proceeding.');
        return;
      }
    }
    
    setError('');
    setStep(step + 1);
    
    // Track step completion
    Mixpanel.trackFeatureAction('Onboarding', 'Next Step', {
      fromStep: currentStep.title,
      toStep: step + 1 < steps.length ? steps[step + 1].title : 'Complete',
      timestamp: new Date().toISOString()
    });
    
    // If moving to a new step, fetch content for that step
    if (step + 1 < steps.length) {
      fetchContent(steps[step + 1].field);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      
      Mixpanel.trackFeatureAction('Onboarding', 'Previous Step', {
        fromStep: currentStep.title,
        toStep: steps[step - 1].title,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleComplete = async () => {
    setPreparingProgram(true);
    setLoading(true);
    setError('');
    try {
      const token = getTokenOrRedirect(setError, navigate, logger);
      if (!token) return;

      // Record start time for minimum display duration
      const startTime = Date.now();

      // Gather all responses
      const onboardingData = {
        ...responses,
        onboardingCompleted: true,
        completedAt: new Date().toISOString()
      };

      logger.debug('Saving onboarding data:', onboardingData);

      // --- Update user profile with onboarding data ---
      try {
        await axios.put(
          `${API_URL}/api/users/profile`,
          onboardingData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        logger.debug('User profile updated successfully.');
      } catch (error) {
        logger.error('Error updating user profile:', error);
        if (error.response && error.response.status === 401) {
          setError('Session expired. Please log in again.');
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        setError('Failed to update profile. Please try again.');
        setLoading(false);
        setPreparingProgram(false);
        return;
      }

      // --- Save onboarding responses ---
      try {
        logger.debug('Attempting to save to /api/onboarding/save endpoint');
        await axios.post(`${API_URL}/api/onboarding/save`, {
          responses: onboardingData,
          completed: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        logger.debug('Saved to /api/onboarding/save endpoint successfully');
      } catch (onboardingError) {
        logger.error('Error saving to onboarding endpoint:', onboardingError);
        // Continue anyway as we've already saved to the profile
      }
      
      // Get current user data to include sport and position information
      let userData = {};
      try {
        const userResponse = await axios.get(`${API_URL}/api/users/info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        userData = userResponse.data;
        logger.debug('Retrieved user data for AI personalization:', userData);
      } catch (userError) {
        logger.error('Error fetching user data:', userError);
        // Continue anyway, we'll just have less personalized content
      }
      
      // Enhance onboarding data with user profile information for better AI personalization
      const enhancedOnboardingData = {
        ...onboardingData,
        sport: userData.sport || onboardingData.sport || '',
        sportPosition: userData.sportPosition || onboardingData.sportPosition || ''
      };
      
      logger.debug('Enhanced onboarding data for AI generation:', enhancedOnboardingData);
      
      // Generate MVP suggestions
      try {
        logger.debug('Generating MVP suggestions...');
        try {
          const mvpResponse = await axios.post(`${API_URL}/api/ai/generate-mvp-suggestions`, enhancedOnboardingData, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 20000 // Increase timeout to 20 seconds
          });
          
          if (mvpResponse.data) {
            logger.debug('MVP suggestions generated:', JSON.stringify(mvpResponse.data, null, 2));
            
            // Ensure the data structure matches what the backend expects
            const mvpData = {
              mission: mvpResponse.data.mission || '',
              vision: Array.isArray(mvpResponse.data.vision) ? mvpResponse.data.vision : [mvpResponse.data.vision || ''],
              principles: Array.isArray(mvpResponse.data.principles) ? mvpResponse.data.principles : [mvpResponse.data.principles || '']
            };
            
            logger.debug('Formatted MVP data for saving:', JSON.stringify(mvpData, null, 2));
            
            // Try to save to the dedicated MVP endpoint first
            try {
              await axios.put(`${API_URL}/api/users/mvp`, mvpData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              logger.debug('MVP saved successfully to dedicated endpoint');
              setMvpGenerated(true);
            } catch (mvpEndpointError) {
              logger.error('Error saving to MVP endpoint:', mvpEndpointError);
              
              // Fallback to profile endpoint
              await axios.put(`${API_URL}/api/users/profile`, { mvp: mvpData }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              logger.debug('MVP saved successfully via profile endpoint');
              setMvpGenerated(true);
            }
          } else {
            throw new Error('Empty MVP response data');
          }
        } catch (mvpApiError) {
          logger.error('Error with MVP API:', mvpApiError);
          throw mvpApiError; // Rethrow to use fallback
        }
      } catch (mvpError) {
        logger.error('Error generating or saving MVP:', mvpError);
        
        // Generate fallback MVP data if AI generation fails
        logger.debug('Generating fallback MVP data...');
        const sportSpecific = onboardingData.sport ? ` as a ${onboardingData.sport}` : '';
        const goalFocused = onboardingData.goals ? ` who can ${onboardingData.goals.toLowerCase()}` : '';
        
        const fallbackMvp = {
          mission: `To become the best athlete${sportSpecific} I can be${goalFocused}`,
          principles: [
            "Excellence",
            "Dedication",
            "Resilience",
            "Growth",
            "Mental Toughness"
          ],
          vision: [`To consistently perform at my best${sportSpecific}, overcome challenges with confidence, and enjoy the journey of athletic development`]
        };
        
        try {
          logger.debug('Saving fallback MVP data to profile...');
          // Try to save to the dedicated MVP endpoint first
          try {
            await axios.put(`${API_URL}/api/users/mvp`, fallbackMvp, {
              headers: { Authorization: `Bearer ${token}` }
            });
            logger.debug('Fallback MVP saved successfully to dedicated endpoint');
            setMvpGenerated(true);
          } catch (mvpEndpointError) {
            logger.error('Error saving to MVP endpoint:', mvpEndpointError);
            
            // Fallback to profile endpoint
            await axios.put(`${API_URL}/api/users/profile`, { mvp: fallbackMvp }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            logger.debug('Fallback MVP saved successfully via profile endpoint');
            setMvpGenerated(true);
          }
        } catch (fallbackMvpError) {
          logger.error('Error saving fallback MVP:', fallbackMvpError);
        }
      }
      
      // Generate mindset suggestions
      try {
        logger.debug('Generating Mindset suggestions...');
        try {
          const mindsetResponse = await axios.post(`${API_URL}/api/ai/generate-mindset-suggestions`, enhancedOnboardingData, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 20000 // Increase timeout to 20 seconds
          });
          
          if (mindsetResponse.data) {
            logger.debug('Mindset suggestions generated, saving to user profile...');
            
            // Ensure all required fields are present
            const completeData = {
              ...mindsetResponse.data,
              bodyLanguage: mindsetResponse.data.bodyLanguage || [],
              beliefs: mindsetResponse.data.beliefs || []
            };
            
            logger.debug('Complete mindset data being saved:', JSON.stringify(completeData, null, 2));
            
            // Try to save to the main mindset endpoint
            try {
              await axios.put(`${API_URL}/api/mindset`, completeData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              // Explicitly save bodyLanguage to MindsetProcess model
              if (completeData.bodyLanguage && completeData.bodyLanguage.length > 0) {
                try {
                  await axios.put(`${API_URL}/api/mindset/process`, { 
                    bodyLanguage: completeData.bodyLanguage 
                  }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  logger.debug('bodyLanguage saved to MindsetProcess successfully');
                } catch (processError) {
                  logger.error('Error saving to MindsetProcess:', processError);
                }
              }
              
              logger.debug('Mindset saved successfully');
              setMindsetGenerated(true);
              
              // Also save to localStorage for immediate access
              localStorage.setItem('mindsetData', JSON.stringify(completeData));
              localStorage.setItem('mindsetDataHasContent', 'true');
            } catch (mindsetSaveError) {
              logger.error('Error saving mindset:', mindsetSaveError);
              // Try profile endpoint as fallback
              logger.debug('Attempting to save mindset data to profile endpoint...');
              await axios.put(`${API_URL}/api/users/profile`, { mindset: completeData }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              logger.debug('Mindset saved to profile successfully');
              setMindsetGenerated(true);
            }
          } else {
            throw new Error('Empty mindset response data');
          }
        } catch (mindsetApiError) {
          logger.error('Error with mindset API:', mindsetApiError);
          throw mindsetApiError; // Rethrow to use fallback
        }
      } catch (mindsetError) {
        logger.error('Error generating mindset suggestions:', mindsetError);
        
        // Generate fallback mindset data if AI generation fails
        logger.debug('Generating fallback mindset data...');
        let focusWord = "Resilience";
        if (onboardingData.desired_mental_state && onboardingData.desired_mental_state.toLowerCase().includes("confident")) {
          focusWord = "Confidence";
        } else if (onboardingData.desired_mental_state && onboardingData.desired_mental_state.toLowerCase().includes("focus")) {
          focusWord = "Focus";
        } else if (onboardingData.goals && onboardingData.goals.toLowerCase().includes("anxiety")) {
          focusWord = "Calm";
        }
        
        const sportSpecific = onboardingData.sport ? `${onboardingData.sport}` : 'athlete';
        
        const fallbackMindset = JSON.parse(localStorage.getItem('mindsetData') || '{}');
        
        try {
          logger.debug('Saving fallback mindset data...');
          logger.debug('Complete fallback data being saved:', JSON.stringify(fallbackMindset, null, 2));
          
          // Save to main mindset endpoint
          await axios.put(`${API_URL}/api/mindset`, fallbackMindset, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Explicitly save bodyLanguage to MindsetProcess model
          if (fallbackMindset.bodyLanguage && fallbackMindset.bodyLanguage.length > 0) {
            try {
              await axios.put(`${API_URL}/api/mindset/process`, { 
                bodyLanguage: fallbackMindset.bodyLanguage 
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              logger.debug('Fallback bodyLanguage saved to MindsetProcess successfully');
            } catch (processError) {
              logger.error('Error saving fallback to MindsetProcess:', processError);
            }
          }
          
          logger.debug('Fallback mindset saved successfully');
          setMindsetGenerated(true);
          
          // Also save to localStorage for immediate access
          localStorage.setItem('mindsetData', JSON.stringify(fallbackMindset));
          localStorage.setItem('mindsetDataHasContent', 'true');
        } catch (fallbackMindsetError) {
          logger.error('Error saving fallback mindset:', fallbackMindsetError);
          // Try profile endpoint as last resort
          try {
            await axios.put(`${API_URL}/api/users/profile`, { mindset: fallbackMindset }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            logger.debug('Fallback mindset saved to profile successfully');
            setMindsetGenerated(true);
          } catch (finalMindsetError) {
            logger.error('All mindset save attempts failed:', finalMindsetError);
          }
        }
      }
      
      // Add default daily habits (directly to profile)
      try {
        logger.debug('Adding default daily habits via profile endpoint...');
        
        // Create default habits based on onboarding data
        const defaultHabits = {
          dailyHabits: [
            {
              name: "Visualization Practice",
              description: "Spend 5 minutes visualizing successful performance",
              completed: false,
              streak: 0
            },
            {
              name: "Deep Breathing",
              description: "3 minutes of deep breathing to reduce stress",
              completed: false,
              streak: 0
            },
            {
              name: "Positive Self-Talk",
              description: "Practice positive affirmations",
              completed: false,
              streak: 0
            },
            {
              name: "Performance Journal",
              description: "Record thoughts and progress",
              completed: false,
              streak: 0
            }
          ]
        };
        
        await axios.put(`${API_URL}/api/users/profile`, { habits: defaultHabits }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        logger.debug('Default habits added successfully via profile endpoint');
        setHabitsGenerated(true);
      } catch (habitsError) {
        logger.error('Error adding default habits:', habitsError);
        // Still mark as generated even if we couldn't save them
        setHabitsGenerated(true);
      }
      
      // Call the function to generate and save the Mindset Audio
      try {
        console.log('ONBOARDING: Starting Mindset Audio generation with data:', {
          hasUserData: !!userData,
          userDataKeys: userData ? Object.keys(userData) : [],
          mindsetDataFromStorage: localStorage.getItem('mindsetData') ? 'present' : 'missing',
          mindsetDataHasContent: localStorage.getItem('mindsetDataHasContent')
        });
        
        // Get the most recent mindset data - try multiple sources
        let mindsetData = {};
        
        // First, try to get from localStorage (most recent)
        const storedMindsetData = localStorage.getItem('mindsetData');
        if (storedMindsetData) {
          try {
            mindsetData = JSON.parse(storedMindsetData);
            console.log('ONBOARDING: Using mindset data from localStorage:', Object.keys(mindsetData));
          } catch (parseError) {
            console.error('ONBOARDING: Error parsing stored mindset data:', parseError);
          }
        }
        
        // If no stored data or empty, create fallback mindset data
        if (!mindsetData || Object.keys(mindsetData).length === 0) {
          console.log('ONBOARDING: No mindset data found, creating fallback data');
          mindsetData = {
            oneWordFocus: 'Focus',
            keysToKeepItSimple: ['Stay present', 'Trust your training'],
            affirmations: ['I am confident', 'I perform my best under pressure'],
            selfTalk: ['I can do this', 'One play at a time'],
            beliefs: ['I am capable', 'I learn from challenges'],
            identityStatement: {
              iHave: ['the skills to succeed'],
              iAm: ['mentally tough'],
              iLove: ['competing at my best'],
              iGive: ['my full effort']
            }
          };
        }
        
        console.log('ONBOARDING: Calling generateMindsetAudio with:', {
          userDataPresent: !!userData,
          mindsetDataKeys: Object.keys(mindsetData),
          oneWordFocus: mindsetData.oneWordFocus || 'missing'
        });
        
        const mindsetAudioResult = await generateMindsetAudio(userData, mindsetData);
        
        if (mindsetAudioResult) {
          console.log('ONBOARDING: Mindset Audio generation completed successfully:', {
            hasScript: !!mindsetAudioResult.script,
            scriptLength: mindsetAudioResult.script?.length || 0,
            hasSettings: !!mindsetAudioResult.settings
          });
        } else {
          console.warn('ONBOARDING: Mindset Audio generation returned null result');
        }
        
      } catch (audioError) {
        console.error('ONBOARDING: Error in Mindset Audio generation:', {
          error: audioError.message,
          stack: audioError.stack
        });
        logger.error('Error in Mindset Audio generation, continuing onboarding:', audioError);
        // Still mark as generated to avoid blocking onboarding completion
        setAudioGenerated(true);
        localStorage.setItem('onboardingAudioGenerated', 'true');
      }

      // Create age-based breathwork presets for the user
      try {
        await createAgeBasedBreathworkPresets(userData, token);
      } catch (breathworkError) {
        logger.error('Error creating age-based breathwork presets, continuing onboarding:', breathworkError);
      }

      // Call the function to generate and save the Mental Imagery
      try {
        await generateMentalImagery(userData, JSON.parse(localStorage.getItem('mindsetData') || '{}'));
      } catch (mentalImageryError) {
        logger.error('Error in Mental Imagery generation, continuing onboarding:', mentalImageryError);
        // Still mark as generated to avoid blocking onboarding completion
        setMentalImageryGenerated(true);
        localStorage.setItem('onboardingMentalImageryGenerated', 'true');
      }

      // Ensure the preparation screen is shown for at least 5 seconds
      // This gives users time to see the loading screen and understand what's happening
      const elapsedTime = Date.now() - startTime;
      const minimumDisplayTime = 5000; // 5 seconds
      
      if (elapsedTime < minimumDisplayTime) {
        logger.debug(`Waiting additional ${minimumDisplayTime - elapsedTime}ms to ensure preparation screen visibility`);
        await new Promise(resolve => setTimeout(resolve, minimumDisplayTime - elapsedTime));
      }
      
      // Track completion of AI generation
      Mixpanel.trackFeatureAction('Onboarding', 'AI Generation Completed');
      
      // Set generation complete state AFTER all processing is done
      logger.debug('Setting generationComplete to true');
      setGenerationComplete(true);
      
      // Force a re-render to ensure the button appears
      setTimeout(() => {
        logger.debug('Checking if generationComplete state was applied:', generationComplete);
        // Double-check that the state was updated
        if (!generationComplete) {
          logger.debug('generationComplete state was not updated, forcing update');
          setGenerationComplete(true);
        }
      }, 500);
      
      // Update localStorage to mark onboarding as completed
      localStorage.setItem('onboardingCompleted', 'true');
      localStorage.setItem('onboardingNeeded', 'false');
      
      // IMPORTANT: Do not close the modal automatically
      // Let the user click the continue button instead
    } catch (error) {
      logger.error('Unexpected error in handleComplete:', error);
      setError('An unexpected error occurred. Please try again.');
      setPreparingProgram(false);
      setLoading(false);
    }
  };

  // Phase 2 - Set flag for manual Mental Imagery audio generation after onboarding
  const generateMentalImageryAudioAsync = async (mentalImageryData) => {
    try {
      if (!mentalImageryData || !mentalImageryData.script || !mentalImageryData.settings) {
        logger.error('Invalid mental imagery data for audio generation');
        return false;
      }
      
      // Just set the flag for manual audio generation
      // No automatic audio generation as per new requirements
      localStorage.setItem('onboardingMentalImageryNeedsAudio', 'true');
      logger.debug('Mental imagery script saved for manual audio generation');
      
      return true;
    } catch (error) {
      logger.error('Setting up manual audio generation failed:', error);
      return false;
    }
  };

  const handleContinueToDashboard = async () => {
    Mixpanel.track('Onboarding Completed');
    logger.debug('Continuing to dashboard...');
    
    // PHASE 2 SEQUENTIAL AUDIO GENERATION - RE-ENABLED WITH IMPROVED TIMING
    // Generate Mindset Audio first, then upon completion, generate Mental Imagery audio
    logger.info('Starting Phase 2: Sequential Audio Generation');
    
    // Set initial generation flags for Dashboard communication
    localStorage.setItem('mindsetAudioGenerating', 'true');
    localStorage.setItem('mentalImageryAudioWaiting', 'true');
    
    // Check if we need to generate Mindset Audio
    const needsMindsetAudio = localStorage.getItem('onboardingMindsetNeedsAudio') === 'true';
    const mindsetAudioId = localStorage.getItem('onboardingMindsetAudioId');
    
    if (needsMindsetAudio && mindsetAudioId) {
      logger.info('Phase 2: Starting Mindset Audio generation first...');
      
      // Start Mindset Audio generation
      generateMindsetAudioAsync()
        .then(mindsetSuccess => {
          if (mindsetSuccess) {
            logger.info('Phase 2: Mindset Audio generation completed successfully');
            localStorage.setItem('mindsetAudioGenerating', 'false');
            localStorage.setItem('mindsetAudioReady', 'true');
            
            // Now start Mental Imagery audio generation
            const needsMentalImageryAudio = localStorage.getItem('onboardingMentalImageryNeedsAudio') === 'true';
            const mentalImageryId = localStorage.getItem('onboardingMentalImageryId');
            
            if (needsMentalImageryAudio && mentalImageryId) {
              logger.info('Phase 2: Starting Mental Imagery audio generation after Mindset Audio completion...');
              localStorage.setItem('mentalImageryAudioWaiting', 'false');
              localStorage.setItem('mentalImageryAudioGenerating', 'true');
              
              // Get the mental imagery data from the previous phase
              const mentalImageryData = {
                id: mentalImageryId,
                script: localStorage.getItem('onboardingMentalImageryScript'),
                title: user?.sport && user?.sportPosition ? 
                  `${user.sport} ${user.sportPosition} Mental Imagery` : 
                  'Personalized Mental Imagery',
                settings: JSON.parse(localStorage.getItem('onboardingMentalImagerySettings') || '{}')
              };
              
              // Start Mental Imagery audio generation
              generateMentalImageryAudioAsync(mentalImageryData)
                .then(mentalImagerySuccess => {
                  if (mentalImagerySuccess) {
                    logger.info('Phase 2: Mental Imagery audio generation completed successfully');
                    localStorage.setItem('mentalImageryAudioGenerating', 'false');
                    localStorage.setItem('mentalImageryAudioReady', 'true');
                  } else {
                    logger.warn('Phase 2: Mental Imagery audio generation failed');
                    localStorage.setItem('mentalImageryAudioGenerating', 'false');
                    localStorage.setItem('mentalImageryAudioError', 'true');
                  }
                })
                .catch(error => {
                  logger.error('Error in Mental Imagery audio generation:', error);
                  localStorage.setItem('mentalImageryAudioGenerating', 'false');
                  localStorage.setItem('mentalImageryAudioError', 'true');
                });
            }
          } else {
            logger.warn('Phase 2: Mindset Audio generation failed');
            localStorage.setItem('mindsetAudioGenerating', 'false');
            localStorage.setItem('mindsetAudioError', 'true');
            
            // Still try Mental Imagery if needed, even if Mindset Audio failed
            const needsMentalImageryAudio = localStorage.getItem('onboardingMentalImageryNeedsAudio') === 'true';
            const mentalImageryId = localStorage.getItem('onboardingMentalImageryId');
            
            if (needsMentalImageryAudio && mentalImageryId) {
              logger.info('Phase 2: Starting Mental Imagery audio generation despite Mindset Audio failure...');
              localStorage.setItem('mentalImageryAudioWaiting', 'false');
              localStorage.setItem('mentalImageryAudioGenerating', 'true');
              
              const mentalImageryData = {
                id: mentalImageryId,
                script: localStorage.getItem('onboardingMentalImageryScript'),
                title: user?.sport && user?.sportPosition ? 
                  `${user.sport} ${user.sportPosition} Mental Imagery` : 
                  'Personalized Mental Imagery',
                settings: JSON.parse(localStorage.getItem('onboardingMentalImagerySettings') || '{}')
              };
              
              generateMentalImageryAudioAsync(mentalImageryData)
                .then(mentalImagerySuccess => {
                  if (mentalImagerySuccess) {
                    logger.info('Phase 2: Mental Imagery audio generation completed successfully');
                    localStorage.setItem('mentalImageryAudioGenerating', 'false');
                    localStorage.setItem('mentalImageryAudioReady', 'true');
                  } else {
                    logger.warn('Phase 2: Mental Imagery audio generation failed');
                    localStorage.setItem('mentalImageryAudioGenerating', 'false');
                    localStorage.setItem('mentalImageryAudioError', 'true');
                  }
                })
                .catch(error => {
                  logger.error('Error in Mental Imagery audio generation:', error);
                  localStorage.setItem('mentalImageryAudioGenerating', 'false');
                  localStorage.setItem('mentalImageryAudioError', 'true');
                });
            }
          }
        })
        .catch(error => {
          logger.error('Error in Mindset Audio generation:', error);
          localStorage.setItem('mindsetAudioGenerating', 'false');
          localStorage.setItem('mindsetAudioError', 'true');
        });
    } else {
      logger.debug('Phase 2: No Mindset Audio generation needed, checking Mental Imagery...');
      localStorage.setItem('mindsetAudioGenerating', 'false');
      
      // If no Mindset Audio needed, proceed directly to Mental Imagery if needed
      const needsMentalImageryAudio = localStorage.getItem('onboardingMentalImageryNeedsAudio') === 'true';
      const mentalImageryId = localStorage.getItem('onboardingMentalImageryId');
      
      if (needsMentalImageryAudio && mentalImageryId) {
        logger.info('Phase 2: Starting Mental Imagery audio generation (no Mindset Audio needed)...');
        localStorage.setItem('mentalImageryAudioWaiting', 'false');
        localStorage.setItem('mentalImageryAudioGenerating', 'true');
        
        const mentalImageryData = {
          id: mentalImageryId,
          script: localStorage.getItem('onboardingMentalImageryScript'),
          title: user?.sport && user?.sportPosition ? 
            `${user.sport} ${user.sportPosition} Mental Imagery` : 
            'Personalized Mental Imagery',
          settings: JSON.parse(localStorage.getItem('onboardingMentalImagerySettings') || '{}')
        };
        
        generateMentalImageryAudioAsync(mentalImageryData)
          .then(mentalImagerySuccess => {
            if (mentalImagerySuccess) {
              logger.info('Phase 2: Mental Imagery audio generation completed successfully');
              localStorage.setItem('mentalImageryAudioGenerating', 'false');
              localStorage.setItem('mentalImageryAudioReady', 'true');
            } else {
              logger.warn('Phase 2: Mental Imagery audio generation failed');
              localStorage.setItem('mentalImageryAudioGenerating', 'false');
              localStorage.setItem('mentalImageryAudioError', 'true');
            }
          })
          .catch(error => {
            logger.error('Error in Mental Imagery audio generation:', error);
            localStorage.setItem('mentalImageryAudioGenerating', 'false');
            localStorage.setItem('mentalImageryAudioError', 'true');
          });
      } else {
        logger.debug('Phase 2: No Mental Imagery audio generation needed');
        localStorage.setItem('mentalImageryAudioWaiting', 'false');
      }
    }
    
    logger.debug('User clicked Continue to Dashboard');
    userClickedContinue.current = true;
    
    // Track this event in Mixpanel
    Mixpanel.trackFeatureAction('Onboarding', 'Completed and Navigated to Dashboard');
    
    // Ensure onboarding is marked as completed in localStorage
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.setItem('onboardingNeeded', 'false');
    
    // Navigate to the dashboard
    navigate('/dashboard');
  };

  const renderPreparingProgram = () => {
    return (
      <div className="text-center py-8">
        <h3 className="text-2xl font-bold mb-6">Preparing Your Personalized Program</h3>
        
        <div className="flex flex-col items-center space-y-8">
          <div className="w-full max-w-md">
            <div className="flex items-center mb-2">
              <div className="w-6 h-6 mr-3 flex-shrink-0">
                {mvpGenerated ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <div className="loading loading-spinner loading-xs text-blue-500"></div>
                )}
              </div>
              <span className="font-medium">Creating your Mission, Vision & Principles</span>
            </div>
            
            <div className="flex items-center mb-2">
              <div className="w-6 h-6 mr-3 flex-shrink-0">
                {mindsetGenerated ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <div className={`${mvpGenerated ? "loading loading-spinner loading-xs text-blue-500" : "w-6 h-6 bg-gray-200 rounded-full"}`}></div>
                )}
              </div>
              <span className={`font-medium ${!mvpGenerated && "text-gray-400"}`}>Developing your Performance Mindset</span>
            </div>
            
            <div className="flex items-center mb-2">
              <div className="w-6 h-6 mr-3 flex-shrink-0">
                {habitsGenerated ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <div className={`${mindsetGenerated ? "loading loading-spinner loading-xs text-blue-500" : "w-6 h-6 bg-gray-200 rounded-full"}`}></div>
                )}
              </div>
              <span className={`font-medium ${!mindsetGenerated && "text-gray-400"}`}>Setting up Daily Mental Training Habits</span>
            </div>
            
            <div className="flex items-center mb-2">
              <div className="w-6 h-6 mr-3 flex-shrink-0">
                {audioGenerated ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <div className={`${habitsGenerated ? "loading loading-spinner loading-xs text-blue-500" : "w-6 h-6 bg-gray-200 rounded-full"}`}></div>
                )}
              </div>
              <span className={`font-medium ${!habitsGenerated && "text-gray-400"}`}>Creating your Mindset Audio</span>
            </div>
            
            <div className="flex items-center mb-2">
              <div className="w-6 h-6 mr-3 flex-shrink-0">
                {mentalImageryGenerated ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <div className={`${audioGenerated ? "loading loading-spinner loading-xs text-blue-500" : "w-6 h-6 bg-gray-200 rounded-full"}`}></div>
                )}
              </div>
              <span className={`font-medium ${!audioGenerated && "text-gray-400"}`}>Creating your Mental Imagery Session</span>
            </div>
          </div>
          
          {generationComplete && (
            <div className="mt-8 animate-fadeIn">
              <p className="text-lg text-green-600 font-medium mb-4">All set! Your personalized program is ready.</p>
              <button
                onClick={handleContinueToDashboard}
                className="btn btn-primary btn-lg px-8"
                ref={el => {
                  if (el && userClickedContinue.current === false) {
                    logger.debug('Continue button is now visible and clickable');
                  }
                }}
              >
                Continue to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Generate and save a personalized Mental Imagery for the user (Phase 1 - Script Only)
  const generateMentalImagery = async (userData, mindsetData) => {
    try {
      logger.debug('Starting Mental Imagery script generation (Phase 1)...');
      const token = localStorage.getItem('token');
      
      if (!token) {
        logger.error('No authentication token found for Mental Imagery generation');
        throw new Error('Authentication token not found');
      }
      
      // Extract the necessary data for the mental imagery
      const name = userData.name || 'Athlete';
      const sport = userData.sport || 'your sport';
      const position = userData.sportPosition || 'athlete';
      const birthYear = userData.birthYear || null;
      
      // Use mindset data for keys, or fallback to default values
      const keys = [
        mindsetData.oneWordFocus || 'Focus',
        mindsetData.affirmations?.[0] || 'I am confident',
        mindsetData.beliefs?.[0] || 'I perform my best under pressure'
      ];
      
      logger.debug('Generating Mental Imagery script with data:', { name, sport, position, keys });
      
      // Call the API to generate the script
      const scriptResponse = await axios.post(
        `${API_URL}/api/ai/generate-mental-imagery`,
        { name, sport, position, keys, birthYear },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 // Increase timeout to 60 seconds
        }
      );
      
      if (!scriptResponse.data || !scriptResponse.data.script) {
        logger.error('Invalid response from Mental Imagery generation API');
        // Mark as generated but failed to avoid blocking onboarding
        setMentalImageryGenerated(true);
        localStorage.setItem('onboardingMentalImageryGenerated', 'false');
        return null;
      }

      const script = scriptResponse.data.script;
      logger.debug('Mental Imagery script generated successfully');
      
      // Save the mental imagery to the user's profile with a "pending" audioUrl
      const mentalImageryData = {
        title: `${sport} ${position} Mental Imagery`,
        script: script,
        audioUrl: 'pending', // Mark as pending for async generation
        settings: {
          voice: 'calm',
          background: 'ambient',
          pace: 'medium',
          duration: 10
        }
      };
      
      const saveResponse = await axios.post(
        `${API_URL}/api/ai/mental-imagery`,
        mentalImageryData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      logger.debug('Mental Imagery saved successfully with pending audio:', saveResponse.data);
      
      // Save the ID for easy access on the dashboard and for Phase 2
      if (saveResponse.data && saveResponse.data._id) {
        localStorage.setItem('onboardingMentalImageryId', saveResponse.data._id);
        localStorage.setItem('onboardingMentalImageryGenerated', 'true');
        localStorage.setItem('onboardingMentalImageryNeedsAudio', 'true'); // Flag for Phase 2
        
        // Store script and settings for async audio generation
        localStorage.setItem('onboardingMentalImageryScript', script);
        localStorage.setItem('onboardingMentalImagerySettings', JSON.stringify(mentalImageryData.settings));
        
        setMentalImageryGenerated(true);
        
        // Return the saved mental imagery data for Phase 2
        return {
          id: saveResponse.data._id,
          script: script,
          settings: mentalImageryData.settings
        };
      } else {
        throw new Error('Failed to get Mental Imagery ID from save response');
      }
    } catch (error) {
      logger.error('Mental Imagery generation failed', error);
      // Log the error stack trace for debugging
      logger.error('Error stack trace:', error);
      // Still mark as generated to avoid blocking onboarding completion
      setMentalImageryGenerated(true);
      localStorage.setItem('onboardingMentalImageryGenerated', 'false');
      return null;
    }
  };

// Generate and save a personalized Mindset Audio for the user
const generateMindsetAudio = async (userData, mindsetData) => {
  logger.debug('Starting Mindset Audio generation (Phase 1: Script Only)...');
  setAudioGenerated(false);
  
  // DUPLICATE PREVENTION: Check if we already have a Mindset Audio for this user
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    // Check for existing Mindset Audio records to prevent duplicates
    logger.debug('Checking for existing Mindset Audio records before generation...');
    try {
      const existingResponse = await axios.get(`${API_URL}/api/ai/audio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (existingResponse.data && Array.isArray(existingResponse.data)) {
        const existingMindsetAudios = existingResponse.data.filter(audio => 
          audio.type === 'mindset' && audio.script && audio.script.trim().length > 0
        );
        
        if (existingMindsetAudios.length > 0) {
          logger.warn(`Found ${existingMindsetAudios.length} existing Mindset Audio record(s). Using existing instead of creating duplicate.`);
          const existingAudio = existingMindsetAudios[0]; // Use the first existing one
          
          // Store the existing ID for dashboard access
          localStorage.setItem('onboardingMindsetAudioId', existingAudio._id);
          localStorage.setItem('onboardingMindsetNeedsAudio', 'true');
          localStorage.setItem('onboardingAudioGenerated', 'true');
          
          setAudioGenerated(true);
          
          return {
            id: existingAudio._id,
            script: existingAudio.script,
            settings: existingAudio.settings || {
              voice: 'calm',
              background: 'none',
              pace: 'medium',
              duration: 5
            }
          };
        }
      }
    } catch (checkError) {
      logger.warn('Error checking for existing Mindset Audio records, proceeding with generation:', checkError);
      // Continue with generation if we can't check for existing records
    }
    
    logger.debug('No existing Mindset Audio records found, proceeding with generation...');
    
    // Step 1: Generate a personalized script based on user's MVP and Mindset data
    logger.debug('Generating personalized audio script');
    
    let script = '';
    try {
      // Try primary script generation endpoint
      try {
        logger.debug('Attempting primary script generation endpoint');
        const scriptResponse = await axios.post(
          `${API_URL}/api/ai/generate-script`,
          {
            settings: {
              voice: 'calm',      // calm voice
              background: 'none', // no background sound
              pace: 'medium',     // medium pace
              duration: 5         // 5 minutes duration
            },
            userData: {
              name: userData.name || '',
              sport: userData.sport || '',
              sportPosition: userData.sportPosition || '',
              mission: userData.mission || '',
              vision: userData.vision || '',
              oneWordFocus: mindsetData.oneWordFocus || '',
              keysToKeepItSimple: mindsetData.keysToKeepItSimple || [],
              affirmations: mindsetData.affirmations || [],
              selfTalk: mindsetData.selfTalk || [],
              beliefs: mindsetData.beliefs || [],
              identityStatement: mindsetData.identityStatement || {
                iHave: [],
                iAm: [],
                iLove: [],
                iGive: []
              }
            }
          },
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 30000
          }
        );
        
        if (scriptResponse.data && scriptResponse.data.script) {
          script = scriptResponse.data.script;
          logger.debug('Script generated successfully from primary endpoint');
        } else {
          throw new Error('Invalid response format from primary endpoint');
        }
      } catch (primaryScriptError) {
        logger.warn('Primary script generation endpoint failed, trying fallback', primaryScriptError);
        
        // Try fallback script endpoint
        try {
          logger.debug('Attempting fallback script generation endpoint');
          const fallbackScriptResponse = await axios.post(
            `${API_URL}/api/ai/mindset-script`,
            {
              settings: {
                voice: 'calm',
                background: 'none',
                pace: 'medium',
                duration: 5
              },
              userData: {
                name: userData.name || '',
                sport: userData.sport || '',
                sportPosition: userData.sportPosition || '',
                mindset: mindsetData
              }
            },
            { 
              headers: { Authorization: `Bearer ${token}` },
              timeout: 30000
            }
          );
          
          if (fallbackScriptResponse.data && fallbackScriptResponse.data.script) {
            script = fallbackScriptResponse.data.script;
            logger.debug('Script generated successfully from fallback endpoint');
          } else {
            throw new Error('Invalid response format from fallback endpoint');
          }
        } catch (fallbackScriptError) {
          logger.error('All script generation endpoints failed, using local fallback', fallbackScriptError);
          // Generate a simple script locally as a last resort
          script = generateFallbackScript(userData, mindsetData);
          logger.debug('Using generated fallback script');
        }
      }
      
      // Save the script to database with pending audio (Phase 1) - matching Mental Imagery approach
      const settings = {
        voice: 'calm',
        background: 'none',
        pace: 'medium',
        duration: 5
      };
      
      const mindsetAudioData = {
        title: `${userData.sport || 'Mindset'} Audio - ${userData.name || 'Athlete'}`,
        script: script,
        audioUrl: 'pending', // Mark as pending for Phase 2 audio generation
        settings: settings,
        isScriptOnly: true,
        type: 'mindset' // Fixed: Use 'mindset' instead of 'mindset-audio' to match schema enum
      };
      
      // Save to database using the save-audio endpoint
      const saveResponse = await axios.post(
        `${API_URL}/api/ai/save-audio`,
        mindsetAudioData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      logger.debug('Mindset Audio saved successfully with pending audio:', saveResponse.data);
      
      // Save the ID for easy access on the dashboard and for Phase 2
      if (saveResponse.data && saveResponse.data.id) {
        localStorage.setItem('onboardingMindsetAudioId', saveResponse.data.id);
        localStorage.setItem('onboardingMindsetNeedsAudio', 'true'); // Flag for Phase 2
        localStorage.setItem('onboardingAudioGenerated', 'true');
        
        logger.debug('Mindset Audio script saved successfully (Phase 1 complete)');
        setAudioGenerated(true);
        
        return {
          id: saveResponse.data.id,
          script: script,
          settings: settings
        };
      } else {
        throw new Error('Failed to get Mindset Audio ID from save response');
      }
      
    } catch (scriptError) {
      logger.error('Error generating script:', scriptError);
      // Generate a simple script locally as a last resort
      script = generateFallbackScript(userData, mindsetData);
      logger.debug('Using generated fallback script after error');
      
      // Still try to save the fallback script to database
      try {
        const settings = {
          voice: 'calm',
          background: 'none',
          pace: 'medium',
          duration: 5
        };
        
        const fallbackMindsetData = {
          title: `${userData.sport || 'Mindset'} Audio - ${userData.name || 'Athlete'} (Fallback)`,
          script: script,
          audioUrl: 'pending',
          settings: settings,
          isScriptOnly: true,
          type: 'mindset'
        };
        
        const fallbackSaveResponse = await axios.post(
          `${API_URL}/api/ai/save-audio`,
          fallbackMindsetData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (fallbackSaveResponse.data && fallbackSaveResponse.data.id) {
          localStorage.setItem('onboardingMindsetAudioId', fallbackSaveResponse.data.id);
          localStorage.setItem('onboardingMindsetNeedsAudio', 'true');
          localStorage.setItem('onboardingAudioGenerated', 'true');
          
          setAudioGenerated(true);
          logger.debug('Fallback mindset script saved to database successfully');
          
          return {
            id: fallbackSaveResponse.data.id,
            script: script,
            settings: settings
          };
        }
      } catch (fallbackSaveError) {
        logger.error('Failed to save fallback script to database:', fallbackSaveError);
      }
      
      // Fallback to localStorage only if database save fails
      localStorage.setItem('onboardingMindsetScript', script);
      localStorage.setItem('onboardingMindsetSettings', JSON.stringify({
        voice: 'calm',
        background: 'none',
        pace: 'medium',
        duration: 5
      }));
      localStorage.setItem('onboardingMindsetNeedsAudio', 'true');
      localStorage.setItem('onboardingAudioGenerated', 'true');
      
      setAudioGenerated(true);
      logger.debug('Mindset script saved to localStorage only (database save failed)');
      
      return {
        script: script,
        settings: {
          voice: 'calm',
          background: 'none',
          pace: 'medium',
          duration: 5
        }
      };
    }
  } catch (error) {
    logger.error('Mindset Audio generation failed completely:', error);
    setAudioGenerated(true); // Mark as completed to avoid blocking onboarding
    localStorage.setItem('onboardingAudioGenerated', 'false');
    return null;
  }
};

// Set flag for manual Mindset Audio generation after onboarding completes (Phase 2)
const generateMindsetAudioAsync = async () => {
  try {
    logger.debug('Starting Phase 2: Mindset Audio generation after onboarding');
    
    // Check if we have the necessary data for audio generation
    const mindsetAudioId = localStorage.getItem('onboardingMindsetAudioId');
    const token = localStorage.getItem('token');
    
    if (!mindsetAudioId || !token) {
      logger.error('Missing data for Mindset Audio generation:', { 
        hasMindsetAudioId: !!mindsetAudioId, 
        hasToken: !!token 
      });
      return false;
    }
    
    logger.info('Phase 2: Generating audio for Mindset Audio script with ID:', mindsetAudioId);
    
    // Call the API to generate audio for this specific script
    try {
      // Ensure the API URL is properly formatted
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      
      // Use the individual script audio generation endpoint
      const response = await axios.post(
        `${baseUrl}/api/ai/audio/${mindsetAudioId}/generate`,
        {},
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 300000 // 5 minute timeout for audio generation
        }
      );
      
      if (response.data && response.data.success) {
        logger.info('Phase 2: Mindset Audio generated successfully:', response.data);
        localStorage.removeItem('onboardingMindsetNeedsAudio'); // Clear the flag since we've generated the audio
        return true;
      } else {
        logger.warn('Phase 2: Mindset Audio generation response not successful:', response.data);
        // Fall through to batch processing
      }
    } catch (apiError) {
      logger.error('Phase 2: API error during Mindset Audio generation:', apiError);
      // Continue to batch processing as fallback
    }
    
    // Try the batch processing endpoint as fallback
    try {
      logger.info('Phase 2: Trying batch processing endpoint as fallback');
      
      // Ensure the API URL is properly formatted
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      
      const batchResponse = await axios.post(
        `${baseUrl}/api/ai/audio/process-pending`,
        {},
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 300000 // 5 minute timeout for batch processing
        }
      );
      
      if (batchResponse.data && batchResponse.data.successCount > 0) {
        logger.info('Phase 2: Batch processing successful:', batchResponse.data);
        localStorage.removeItem('onboardingMindsetNeedsAudio');
        
        // Add a delay to ensure backend processing completes
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return true;
      } else {
        logger.warn('Phase 2: Batch processing did not generate any audio:', batchResponse.data);
        localStorage.setItem('onboardingMindsetNeedsAudio', 'true');
        return false;
      }
    } catch (batchError) {
      logger.error('Phase 2: Batch processing also failed:', batchError);
      localStorage.setItem('onboardingMindsetNeedsAudio', 'true');
      return false;
    }
  } catch (error) {
    logger.error('Phase 2: Mindset Audio generation failed:', error);
    // Set the flag for manual generation later
    localStorage.setItem('onboardingMindsetNeedsAudio', 'true');
    return false;
  }
};

  // Create age-based breathwork presets based on user's age
  const createAgeBasedBreathworkPresets = async (userData, token) => {
    try {
      logger.debug('Creating age-based breathwork presets...');
      
      if (!token) {
        logger.error('No authentication token found for creating breathwork presets');
        throw new Error('Authentication token not found');
      }
      
      // Calculate age from birthYear if available
      let age = null;
      if (userData.birthYear) {
        const currentYear = new Date().getFullYear();
        age = currentYear - parseInt(userData.birthYear);
        logger.debug(`Calculated user age: ${age} years old`);
      } else if (userData.age) {
        age = parseInt(userData.age);
        logger.debug(`Using provided user age: ${age} years old`);
      } else {
        logger.warn('No age or birthYear found in user data, using default presets for 16+');
        age = 16; // Default to adult presets if age is unknown
      }
      
      // Define presets based on age groups
      let boxBreathingPreset;
      let customBreathingPreset;
      
      if (age <= 13) {
        // Ages 10-13 presets
        logger.debug('Creating breathwork presets for age group 10-13');
        boxBreathingPreset = {
          name: "Box Breathing",
          inhale: 2,
          hold1: 2,
          exhale: 2,
          hold2: 2,
          cycles: 4
        };
        
        customBreathingPreset = {
          name: "2-4-1 Breathing",
          inhale: 2,
          hold1: 0,
          exhale: 4,
          hold2: 1,
          cycles: 4
        };
      } else if (age <= 16) {
        // Ages 14-16 presets
        logger.debug('Creating breathwork presets for age group 14-16');
        boxBreathingPreset = {
          name: "Box Breathing",
          inhale: 3,
          hold1: 3,
          exhale: 3,
          hold2: 3,
          cycles: 4
        };
        
        customBreathingPreset = {
          name: "4-6-2 Breathing",
          inhale: 4,
          hold1: 0,
          exhale: 6,
          hold2: 2,
          cycles: 4
        };
      } else {
        // Ages 16+ presets
        logger.debug('Creating breathwork presets for age group 16+');
        boxBreathingPreset = {
          name: "Box Breathing",
          inhale: 4,
          hold1: 4,
          exhale: 4,
          hold2: 4,
          cycles: 5
        };
        
        customBreathingPreset = {
          name: "6-8-2 Breathing",
          inhale: 6,
          hold1: 0,
          exhale: 8,
          hold2: 2,
          cycles: 5
        };
      }
      
      // Save the first preset (Box Breathing)
      logger.debug('Saving Box Breathing preset:', boxBreathingPreset);
      await axios.post(
        `${API_URL}/api/breathwork/preset`,
        boxBreathingPreset,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Save the second preset (Custom Breathing)
      logger.debug('Saving custom breathing preset:', customBreathingPreset);
      await axios.post(
        `${API_URL}/api/breathwork/preset`,
        customBreathingPreset,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      logger.debug('Age-based breathwork presets created successfully');
    } catch (error) {
      logger.error('Error creating age-based breathwork presets:', error);
      throw error;
    }
  };
  
  // Generate a fallback script if API calls fail
  const generateFallbackScript = (userData, mindsetData) => {
    const name = userData.name || 'Athlete';
    const sport = userData.sport || 'your sport';
    const position = userData.sportPosition ? ` as a ${userData.sportPosition}` : '';
    const focus = mindsetData.oneWordFocus || 'Focus';
    
    // Get a few affirmations and beliefs if available
    const affirmations = mindsetData.affirmations && mindsetData.affirmations.length > 0 
      ? mindsetData.affirmations.slice(0, 3) 
      : ['I am confident and capable', 'I perform my best under pressure', 'I am mentally tough'];
    
    const beliefs = mindsetData.beliefs && mindsetData.beliefs.length > 0
      ? mindsetData.beliefs.slice(0, 2)
      : ['I am capable of achieving my goals', 'Every practice and competition is an opportunity to improve'];
    
    // Create a simple script
    return `
    Welcome ${name}. This is your personalized mindset audio for ${sport}${position}.
    
    Take a deep breath in... and slowly exhale.
    
    Your one-word focus is ${focus}. Keep this word in mind as you prepare for performance.
    
    Let's reinforce your mindset with these affirmations:
    ${affirmations.map(a => `${a}.`).join('\n')}
    
    Remember these core beliefs:
    ${beliefs.map(b => `${b}.`).join('\n')}
    
    As you continue your journey in ${sport}, remember why you play and what drives you.
    
    Take another deep breath in... and slowly exhale.
    
    You are prepared. You are focused. You are ready.
    `;
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8 flex flex-col h-screen">
        {/* Header */}
        <div className="flex justify-between items-center border-b p-4 bg-white shadow-md">
          <h2 className="text-xl md:text-2xl font-bold text-primary whitespace-nowrap">Welcome to Mental Edge Coach</h2>
          
          {/* Only show close button when not in preparation mode or after generation completes */}
          {(!preparingProgram || generationComplete) && (
            <button 
              onClick={handleContinueToDashboard}
              className="btn btn-sm btn-circle btn-ghost"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        <div className="flex-grow flex flex-col overflow-hidden">
          {/* Enhanced progress bar */}
          {!preparingProgram && (
            <div className="py-2 md:py-4">
              <div className="flex justify-between mb-1">
                <span className="text-xs md:text-sm font-medium text-gray-700">Step {step + 1} of {steps.length}</span>
                <span className="text-xs md:text-sm font-medium text-gray-700">{Math.round((step / (steps.length - 1)) * 100)}% Complete</span>
              </div>
              <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                <div 
                  className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${(step / (steps.length - 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
          
          {/* Content */}
          <div className="flex-grow flex flex-col max-w-3xl mx-auto w-full">
            {preparingProgram ? (
              <div className="flex-grow flex items-center justify-center">
                {renderPreparingProgram()}
              </div>
            ) : (
              <div className="flex flex-col h-full">
                {/* Step heading */}
                <div className="mb-2 md:mb-4">
                  <h2 className="text-xl md:text-2xl font-bold">
                    {currentStep.field === 'introduction' && "Welcome to Mental Edge Coach"}
                    {currentStep.field === 'why_i_play' && "Why Do You Play?"}
                    {currentStep.field === 'journey' && "Your Athletic Journey"}
                    {currentStep.field === 'goals' && "Your Performance Goals"}
                    {currentStep.field === 'current_mental_state' && "Current Mental State"}
                    {currentStep.field === 'desired_mental_state' && "Desired Mental State"}
                    {currentStep.field === 'expectations' && "How We Can Help"}
                  </h2>
                  <p className="text-xs md:text-sm text-gray-600">
                    {currentStep.description}
                  </p>
                  <p className="text-xs md:text-sm text-gray-600 mt-1">
                    {currentStep.instruction}
                  </p>
                </div>
                
                {error && (
                  <div className="alert alert-error mb-2">
                    <span className="text-xs md:text-sm">{error}</span>
                  </div>
                )}
                
                {/* Scrollable content area with auto-scaling */}
                <div className="flex-grow overflow-auto mb-2 pb-2 max-h-[calc(100vh-200px)]">
                  {renderStepContent()}
                </div>
                
                {/* Navigation buttons - fixed at bottom */}
                <div className="fixed bottom-0 left-0 right-0 bg-white py-2 px-4 border-t border-gray-200 z-10">
                  <div className="flex flex-col-reverse md:flex-row justify-between gap-2 max-w-3xl mx-auto">
                    <button
                      onClick={handleBack}
                      disabled={step === 0}
                      className="btn btn-outline btn-neutral w-full md:w-auto border-2 border-blue-500 h-10 px-6"
                    >
                      Back
                    </button>
                    
                    {step < steps.length - 1 ? (
                      <button
                        onClick={handleNext}
                        className="btn btn-primary w-full md:w-auto h-10 px-6 shadow-md hover:shadow-lg"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={handleComplete}
                        disabled={loading}
                        className="btn btn-primary w-full md:w-auto h-10 px-6 shadow-md hover:shadow-lg bg-green-500 hover:bg-green-600"
                      >
                        {loading ? (
                          <>
                            <span className="loading loading-spinner loading-sm mr-2"></span>
                            Preparing...
                          </>
                        ) : "Complete"}
                      </button>
                    )}
                  </div>
                </div>
                {/* Add padding at the bottom to prevent content from being hidden behind fixed buttons */}
                <div className="h-16"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;